import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Alert, Button } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { AuthSpinner, AuthStatusCard } from '../components/OidcSessionGate';
import { clearMfaStepUpFailure } from '../lib/adminMfaStepUp';
import { ADMIN_CONSOLE_ME_QUERY_KEY } from '../lib/adminSession';
import { completeOidcCallback, startOidcAuthorize, type OidcCallbackResult } from '../lib/oidc/oidcSession';

// The transaction is single-use: React StrictMode's double effect must not run the exchange twice.
const inFlight = new Map<string, Promise<OidcCallbackResult>>();

function completeOnce(search: string): Promise<OidcCallbackResult> {
  let p = inFlight.get(search);
  if (!p) {
    p = completeOidcCallback(search);
    inFlight.set(search, p);
  }
  return p;
}

/** `VITE_ADMIN_OIDC_REDIRECT_PATH` (default `/auth/callback`): code → tokens (memory) → original route. */
export function AuthCallbackPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    void completeOnce(location.search).then((result) => {
      if (!active) return;
      if (result.status === 'signed-in') {
        clearMfaStepUpFailure();
        void qc.invalidateQueries({ queryKey: ADMIN_CONSOLE_ME_QUERY_KEY });
        // Drop the code/state from history.
        void navigate(result.returnTo, { replace: true });
      } else if (result.status === 'error') {
        setError(result.message);
      }
    });
    return () => {
      active = false;
    };
  }, [location.search, navigate, qc]);

  if (!error) return <AuthSpinner text="Completing sign-in…" />;
  return (
    <AuthStatusCard>
      <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
        {error}
      </Alert>
      <Button fullWidth variant="contained" onClick={() => void startOidcAuthorize({ kind: 'interactive', returnTo: '/' })}>
        Sign in again
      </Button>
    </AuthStatusCard>
  );
}
